import { Icon, Toggle, Typography } from "@/components/atoms";
import { ClientPaymentMethod } from "@/redux/apis/clientPayment/types";
import { clsx } from "@/utils";
import { getCardBrandLogo, getCardExpiry, getCardIssuerDisplay, getCardLastFour, getCardNicknameDisplay, isCardExpired } from "../helpers";

interface CardRowProps {
  method: ClientPaymentMethod;
  isEditing: boolean;
  isSettingDefault: boolean;
  lockedByInvoice?: boolean;
  onEditCard: (method: ClientPaymentMethod) => void;
  onSetDefault: (method: ClientPaymentMethod) => void;
}

const MaskedLastFour = ({ lastFour }: { lastFour: string }) => (
  <Typography variant="action" className="shrink-0 text-neutral-dark-gray">
    <span aria-hidden className="mr-1 tracking-[3px]">
      ••••••
    </span>
    {lastFour}
  </Typography>
);

const BrandIcon = ({ issuer }: { issuer?: string }) => (
  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-neutral-mid-gray bg-white">
    {getCardBrandLogo(issuer)}
  </div>
);

export const CardRow = ({ method, isEditing, isSettingDefault, lockedByInvoice, onEditCard, onSetDefault }: CardRowProps) => {
  const lastFour = getCardLastFour(method.card_data);
  const expired = isCardExpired(method.card_data);
  const expiry = getCardExpiry(method.card_data);
  const hasNickname = !!method.card_data?.nickname?.trim();
  const showDefaultToggle = isEditing && !lockedByInvoice;

  const defaultToggle = (
    <Toggle
      size="xs"
      checked={method.default}
      disabled={method.default || isSettingDefault}
      onChange={() => onSetDefault(method)}
      className={clsx(method.default && "!bg-primary")}
    />
  );

  return (
    <div
      className={clsx(
        "rounded-lg border p-2.5",
        method.default ? "border-vivid-purple bg-vivid-purple/5" : "border-neutral-mid-gray bg-neutral-surface-gray"
      )}
    >
      {/* Mobile (Figma 5379): icon left, stacked nickname / issuer / masked number / toggle; edit pencil top-right */}
      <div className="relative flex items-start gap-3 lg:hidden">
        <BrandIcon issuer={method.card_data?.issuer} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-center gap-1.5 pr-7">
            <Typography variant="action" className="truncate text-neutral-dark-gray">
              {getCardNicknameDisplay(method)}
            </Typography>
            {method.default && (
              <Typography variant="paragraph" className="shrink-0 text-info">
                (Default)
              </Typography>
            )}
            {expired && (
              <Typography variant="paragraph" className="shrink-0 text-danger">
                (Expired)
              </Typography>
            )}
          </div>
          {hasNickname && (
            <Typography variant="paragraph" className="text-neutral-dark-gray">
              {getCardIssuerDisplay(method.card_data?.issuer)}
            </Typography>
          )}
          <MaskedLastFour lastFour={lastFour} />
          {showDefaultToggle && (
            <div className="mt-1.5 flex items-center gap-2">
              {defaultToggle}
              <Typography variant="small" className="text-neutral-silver-gray">
                Default Payment
              </Typography>
            </div>
          )}
        </div>
        {isEditing && (
          <button
            type="button"
            aria-label="Edit card"
            className="absolute right-0 top-0 shrink-0 text-neutral-dark-gray hover:text-neutral-black"
            onClick={() => onEditCard(method)}
          >
            <Icon name="Edit2" size={20} />
          </button>
        )}
      </div>

      {/* Desktop: card info on the left, masked/expiry (view) or Default toggle (edit) on the right */}
      <div className="hidden items-center gap-3 lg:flex">
        <BrandIcon issuer={method.card_data?.issuer} />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-1.5">
            <Typography variant="action" className="truncate text-neutral-dark-gray">
              {getCardNicknameDisplay(method)}
            </Typography>
            {!isEditing && method.default && (
              <Typography variant="paragraph" className="shrink-0 text-info">
                (Default)
              </Typography>
            )}
            {expired && (
              <Typography variant="paragraph" className="shrink-0 text-danger">
                (Expired)
              </Typography>
            )}
            {isEditing && <MaskedLastFour lastFour={lastFour} />}
            {isEditing && (
              <button
                type="button"
                aria-label="Edit card"
                className="shrink-0 text-neutral-dark-gray hover:text-neutral-black"
                onClick={() => onEditCard(method)}
              >
                <Icon name="Edit2" size={20} />
              </button>
            )}
          </div>
          {isEditing
            ? expiry && (
                <Typography variant="paragraph" className="text-neutral-silver-gray">
                  Expires <span>{expiry}</span>
                </Typography>
              )
            : hasNickname && (
                <Typography variant="paragraph" className="text-neutral-dark-gray">
                  {getCardIssuerDisplay(method.card_data?.issuer)}
                </Typography>
              )}
        </div>
        {isEditing ? (
          showDefaultToggle && (
            <div className="flex shrink-0 items-center gap-2">
              <Typography variant="small" className="text-neutral-silver-gray">
                Default Payment
              </Typography>
              {defaultToggle}
            </div>
          )
        ) : (
          <div className="flex shrink-0 flex-col items-end">
            <MaskedLastFour lastFour={lastFour} />
            {expiry && (
              <Typography variant="paragraph" className="text-neutral-silver-gray">
                Expires <span>{expiry}</span>
              </Typography>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
